/* UFORIQ cart drawer: pure logic (no DOM). Loaded before assets/uq-cart-drawer.js and
 * unit-tested with Node (module.exports at the bottom).
 *   - delivery-area reward tiers and progress
 *   - small order fee hint (the fee replaced the $20 order minimum, 2026-10-21)
 *   - stale Zapiet delivery-date check in America/Chicago
 *   - upsell candidate filtering
 *   - money formatting with the shop's money format
 */
(function (root) {
  'use strict';

  var AREAS = ['austin', 'lubbock', 'nationwide'];

  /* Two separate tiers: free same-day local delivery in Austin and Lubbock, free nationwide
   * shipping. Both are $200 since 2026-10-14 (local was $100). */
  var TIERS = {
    local: {
      kind: 'local',
      threshold: 20000,
      label: 'Free same-day delivery at $200',
      away: "You're {amount} away from free same-day delivery",
      reached: "You've unlocked free same-day delivery",
      aria: 'Progress to free same-day delivery'
    },
    ship: {
      kind: 'ship',
      threshold: 20000,
      label: 'Free shipping at $200',
      away: "You're {amount} away from free shipping",
      reached: "You've unlocked free shipping",
      aria: 'Progress to free shipping'
    }
  };

  /* Small order fee (2026-10-21, replaced the $20 order minimum): the scheduling widget adds one
   * "Small Order Fee" line while the merchandise subtotal (after discount codes, without fee and
   * add-on lines) is under $25, for delivery and shipping. The widget's config
   * (smallOrderFee.threshold) is the source of truth; this is the fallback. */
  var SMALL_ORDER_FEE = {
    thresholdCents: 2500,
    label: 'Small order fee (orders under $25)',
    hint: 'Add {amount} more to remove the small order fee'
  };

  function normalizeArea(area) {
    area = typeof area === 'string' ? area.toLowerCase() : '';
    return AREAS.indexOf(area) === -1 ? null : area;
  }

  function tierForArea(area) {
    area = normalizeArea(area);
    if (area === 'austin' || area === 'lubbock') return TIERS.local;
    if (area === 'nationwide') return TIERS.ship;
    return null;
  }

  /* Which area the drawer banner shows. The scheduling widget's cart method wins over the site's
   * area picker: Checkout-Method "shipping" (for example a ZIP outside every delivery zone) shows
   * the Nationwide shipping tier, "delivery" shows the confirmed zone's metro (falling back to the
   * picked area while no zone is confirmed). No method keeps the area picker.
   * zoneMetro: metro of the Delivery-Location-Id zone, or null.
   * Returns { area, shipping } where area may be null (no area chosen and no method). */
  function bannerArea(area, method, zoneMetro) {
    area = normalizeArea(area);
    method = typeof method === 'string' ? method.toLowerCase() : '';
    if (method === 'shipping') return { area: 'nationwide', shipping: true };
    if (method === 'delivery') {
      var z = normalizeArea(zoneMetro);
      if (z && z !== 'nationwide') return { area: z, shipping: false };
    }
    return { area: area, shipping: false };
  }

  /* totalCents: cart total that counts toward the reward (add-ons already excluded). */
  function rewardProgress(totalCents, area) {
    if (!tierForArea(area)) return null;
    var total = Math.max(0, Math.round(Number(totalCents) || 0));
    var tier = tierForArea(area);
    var remaining = Math.max(0, tier.threshold - total);
    var pct = Math.min(100, Math.floor((total / tier.threshold) * 100));
    return {
      kind: tier.kind,
      threshold: tier.threshold,
      total: total,
      remaining: remaining,
      reached: remaining === 0,
      percent: remaining === 0 ? 100 : pct,
      label: tier.label,
      aria: tier.aria,
      template: remaining === 0 ? tier.reached : tier.away
    };
  }

  function rewardMessage(progress, format) {
    if (!progress) return '';
    return progress.template.replace('{amount}', formatMoney(progress.remaining, format));
  }

  /* Sum of the lines that count toward rewards: everything except the add-on and fee product(s). */
  function rewardsTotal(cart, addonHandles) {
    if (!cart) return 0;
    var skip = toList(addonHandles);
    var addons = 0;
    (cart.items || []).forEach(function (it) {
      if (skip.indexOf(it.handle) !== -1) addons += Number(it.final_line_price || it.line_price || 0);
    });
    return Math.max(0, Number(cart.total_price || 0) - addons);
  }

  /* The small order fee hint for a merchandise total (fee and add-ons already excluded).
   * thresholdCents: the widget's threshold when known (falls back to $25).
   * -> { remaining, message } while under the threshold, null at or above it (or for an empty total). */
  function smallOrderFeeHint(totalCents, format, thresholdCents) {
    var threshold = Math.round(Number(thresholdCents) || SMALL_ORDER_FEE.thresholdCents);
    var total = Math.max(0, Math.round(Number(totalCents) || 0));
    if (total >= threshold) return null;
    var remaining = threshold - total;
    return { remaining: remaining, message: SMALL_ORDER_FEE.hint.replace('{amount}', formatMoney(remaining, format)) };
  }

  /* ---------- Dates ---------- */

  /* Today's calendar date in America/Chicago as YYYY-MM-DD. */
  function chicagoToday(now) {
    var d = now instanceof Date ? now : new Date(now == null ? Date.now() : now);
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit'
      }).formatToParts(d);
      var o = {};
      parts.forEach(function (p) { o[p.type] = p.value; });
      return o.year + '-' + o.month + '-' + o.day;
    } catch (e) {
      // Very old browsers without time zone data: fall back to a fixed UTC-6 offset.
      var c = new Date(d.getTime() - 6 * 3600 * 1000);
      return c.getUTCFullYear() + '-' + pad(c.getUTCMonth() + 1) + '-' + pad(c.getUTCDate());
    }
  }

  /* Zapiet stores Delivery-Date as YYYY/MM/DD. Returns YYYY-MM-DD or null when it isn't a real date. */
  function parseDeliveryDate(value) {
    if (typeof value !== 'string') return null;
    var m = value.trim().match(/^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})$/);
    if (!m) return null;
    var y = +m[1], mo = +m[2], d = +m[3];
    var dt = new Date(Date.UTC(y, mo - 1, d));
    if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
    return y + '-' + pad(mo) + '-' + pad(d);
  }

  /* True only when the saved date is a valid date strictly before today in Chicago. Today and
   * future dates, blanks and unrecognised formats are left alone. */
  function isStaleDeliveryDate(value, now) {
    var iso = parseDeliveryDate(value);
    if (!iso) return false;
    return iso < chicagoToday(now);
  }

  /* The attribute update for a cart: clears Zapiet's date, time and slot when the date is stale
   * (Delivery-Location-Id and Checkout-Method are never touched) and writes Delivery-Area when it
   * differs. Returns null when nothing needs to change. */
  function attributeUpdate(attributes, area, now) {
    attributes = attributes || {};
    var out = {};
    var stale = isStaleDeliveryDate(attributes['Delivery-Date'], now);
    if (stale) {
      out['Delivery-Date'] = '';
      out['Delivery-Time'] = '';
      out['Delivery-Slot-Id'] = '';
    }
    area = normalizeArea(area);
    if (area && attributes['Delivery-Area'] !== area) out['Delivery-Area'] = area;
    return Object.keys(out).length ? { attributes: out, staleCleared: stale } : null;
  }

  /* ---------- Upsells ---------- */

  var BLOCKED = /thca|flower|pre[\s_-]?rolls?|concentrate|rosin|resin/i;
  var FEE_TAG = /^(opus-addons?|ego-addons?|add-?ons?|fees?|priority[\s-]?delivery)$/i;

  function toList(v) {
    if (Array.isArray(v)) return v.filter(Boolean);
    if (typeof v === 'string') return v.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    return [];
  }

  function isBlockedProduct(p) {
    var tags = toList(p.tags);
    var hay = [p.title || '', p.url || '', p.handle || '', tags.join(' ')].join(' ');
    return BLOCKED.test(hay);
  }

  function isGiftCard(p) {
    if (p.giftCard === true || p.giftCard === 'true') return true;
    return /gift[\s_-]?card/i.test([p.type || '', p.handle || '', p.title || ''].join(' '));
  }

  function isFeeOrAddon(p, addonHandles) {
    var handle = String(p.handle || '');
    if (toList(addonHandles).indexOf(handle) !== -1) return true;
    if (/^add-?ons?-/i.test(handle) || /(^|-)fees?(-|$)/i.test(handle)) return true;
    if (/\bfees?\b/i.test(p.title || '')) return true;
    return toList(p.tags).some(function (t) { return FEE_TAG.test(t); });
  }

  /* candidates: product summaries in priority order (complementary results first, then related).
   * Each needs { id, handle, title, url, tags, type, available, giftCard }.
   * Returns at most `limit` products, de-duplicated, without cart items, unavailable or sold-out
   * products, gift cards, fee/add-on products or blocked categories. */
  function filterUpsells(candidates, opts) {
    opts = opts || {};
    var limit = opts.limit == null ? 6 : opts.limit;
    var inCart = toList(opts.cartProductIds).map(String);
    var seen = {};
    var out = [];
    (candidates || []).forEach(function (p) {
      if (!p || out.length >= limit) return;
      var id = String(p.id);
      if (!id || seen[id]) return;
      seen[id] = true;
      if (inCart.indexOf(id) !== -1) return;
      if (!(p.available === true || p.available === 'true')) return;
      if (isGiftCard(p) || isFeeOrAddon(p, opts.addonHandles) || isBlockedProduct(p)) return;
      out.push(p);
    });
    return out;
  }

  /* Which products to ask recommendations for: every distinct product in the cart (newest first),
   * capped so a big cart doesn't fire dozens of requests. */
  function recommendationSeeds(cartProductIds, max) {
    var seen = {};
    var out = [];
    toList(cartProductIds).forEach(function (id) {
      id = String(id);
      if (seen[id]) return;
      seen[id] = true;
      out.push(id);
    });
    return out.slice(0, max == null ? 4 : max);
  }

  /* ---------- Money ---------- */

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  function formatMoney(cents, format) {
    format = typeof format === 'string' && format ? format : '${{amount}}';
    cents = Math.round(Number(cents) || 0);
    function delimit(number, precision, thousands, decimal) {
      var negative = number < 0;
      var fixed = (Math.abs(number) / 100).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return (negative ? '-' : '') + whole + (parts[1] ? decimal + parts[1] : '');
    }
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var value;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': value = delimit(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = delimit(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = delimit(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = delimit(cents, 2, "'", '.'); break;
      default: value = delimit(cents, 2, ',', '.');
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value).replace(/<[^>]*>/g, '');
  }

  var api = {
    AREAS: AREAS,
    TIERS: TIERS,
    SMALL_ORDER_FEE: SMALL_ORDER_FEE,
    smallOrderFeeHint: smallOrderFeeHint,
    normalizeArea: normalizeArea,
    tierForArea: tierForArea,
    bannerArea: bannerArea,
    rewardProgress: rewardProgress,
    rewardMessage: rewardMessage,
    rewardsTotal: rewardsTotal,
    chicagoToday: chicagoToday,
    parseDeliveryDate: parseDeliveryDate,
    isStaleDeliveryDate: isStaleDeliveryDate,
    attributeUpdate: attributeUpdate,
    isBlockedProduct: isBlockedProduct,
    isGiftCard: isGiftCard,
    isFeeOrAddon: isFeeOrAddon,
    filterUpsells: filterUpsells,
    recommendationSeeds: recommendationSeeds,
    formatMoney: formatMoney
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.UqCartLogic = api;
})(typeof window !== 'undefined' ? window : this);
